// src/components/WebSocketDebug.tsx
import React, { useState, useEffect } from 'react';
import { ConnectionStatus } from '../hooks/useWebSocket';
import { WebSocketDebugProps } from '../types/Message';

const WebSocketDebug: React.FC<WebSocketDebugProps> = ({
  status,
  lastMessage,
  sessionId,
  onSendTest,
  consciousnessState
}) => {
  const [expanded, setExpanded] = useState(false);
  const [logs, setLogs] = useState<string[]>([]);
  
  // Add current status to logs when it changes
  useEffect(() => {
    const timestamp = new Date().toISOString();
    setLogs(prev => [...prev, `[${timestamp}] Connection status: ${status}`]);
  }, [status]);
  
  // Add received message to logs
  useEffect(() => {
    if (lastMessage) {
      const timestamp = new Date().toISOString();
      let messageInfo = 'Unknown message format';
      
      try {
        if (lastMessage.parsed) {
          messageInfo = `Type: ${lastMessage.parsed.type}, Action: ${lastMessage.parsed.action || 'none'}`;
        } else if (typeof lastMessage.data === 'string') {
          const data = JSON.parse(lastMessage.data);
          messageInfo = `Type: ${data.type}, Action: ${data.action || 'none'}`;
        }
      } catch (e) {
        messageInfo = `Raw data: ${typeof lastMessage.data === 'string' ? lastMessage.data.substring(0, 50) : 'non-string data'}`;
      }
      
      setLogs(prev => [...prev, `[${timestamp}] Received: ${messageInfo}`]);
      
      // Limit logs to last 100 entries
      if (logs.length > 100) {
        setLogs(prev => prev.slice(prev.length - 100));
      }
    }
  }, [lastMessage, logs.length]);
  
  // Copy logs to clipboard
  const copyLogs = () => {
    navigator.clipboard.writeText(logs.join('\n'));
    alert('Logs copied to clipboard');
  };
  
  // Clear logs
  const clearLogs = () => {
    setLogs([]);
  };
  
  return (
    <div style={styles.container}>
      <div 
        style={{
          ...styles.header,
          borderBottom: expanded ? '1px solid #ccc' : 'none'
        }} 
        onClick={() => setExpanded(!expanded)}
      >
        <h3 style={styles.title}>WebSocket Debug</h3>
        <span>{expanded ? '▼' : '▶'}</span>
      </div>
      
      {expanded && (
        <div style={styles.content}>
          <div style={styles.info}>
            <div>Status: <span style={getStatusStyle(status)}>{status}</span></div>
            <div>Session ID: {sessionId || 'Not connected'}</div>
            {consciousnessState && (
              <div style={{ marginTop: '5px' }}>
                <div>Consciousness: {consciousnessState.isConscious ? '🟢 Active' : '🔴 Inactive'}</div>
                {consciousnessState.currentThought && (
                  <div style={{fontSize: '10px', marginTop: '2px', color: '#666'}}>
                    Thought: {consciousnessState.currentThought.substring(0, 50)}...
                  </div>
                )}
              </div>
            )}
          </div>
          
          <div style={styles.actions}>
            <button style={styles.button} onClick={onSendTest}>Send Test Message</button>
            <button style={styles.button} onClick={copyLogs}>Copy Logs</button>
            <button style={styles.button} onClick={clearLogs}>Clear Logs</button>
          </div>
          
          <div style={styles.logs}>
            <h4>Connection Logs:</h4>
            <pre style={styles.logContent}>
              {logs.join('\n')}
            </pre>
          </div>
        </div>
      )}
    </div>
  );
};

// Get color for connection status
const getStatusStyle = (status: ConnectionStatus): React.CSSProperties => {
  switch (status) {
    case ConnectionStatus.CONNECTED:
      return { color: 'green', fontWeight: 'bold' };
    case ConnectionStatus.CONNECTING:
    case ConnectionStatus.RECONNECTING:
      return { color: 'orange', fontWeight: 'bold' };
    case ConnectionStatus.ERROR:
      return { color: 'red', fontWeight: 'bold' };
    default:
      return { color: 'gray', fontWeight: 'bold' };
  }
};

// Inline styles
const styles = {
  container: {
    position: 'fixed' as const,
    bottom: '10px',
    right: '10px',
    width: '350px',
    backgroundColor: '#f0f0f0',
    border: '1px solid #ccc',
    borderRadius: '5px',
    boxShadow: '0 0 10px rgba(0,0,0,0.1)',
    zIndex: 1000,
  },
  header: {
    display: 'flex' as const,
    justifyContent: 'space-between' as const,
    alignItems: 'center' as const,
    padding: '10px',
    backgroundColor: '#e0e0e0',
    cursor: 'pointer' as const,
    borderRadius: '5px 5px 0 0',
  },
  title: {
    margin: 0,
    fontSize: '14px',
  },
  content: {
    padding: '10px',
  },
  info: {
    marginBottom: '10px',
    fontSize: '12px',
  },
  actions: {
    display: 'flex' as const,
    gap: '5px',
    marginBottom: '10px',
  },
  button: {
    padding: '5px 10px',
    fontSize: '12px',
    backgroundColor: '#5b6af5',
    color: 'white',
    border: 'none',
    borderRadius: '3px',
    cursor: 'pointer' as const,
  },
  logs: {
    fontSize: '12px',
  },
  logContent: {
    height: '200px',
    overflow: 'auto' as const,
    backgroundColor: '#2f3640',
    color: '#f5f6fa',
    padding: '5px',
    borderRadius: '3px',
    margin: 0,
    whiteSpace: 'pre-wrap' as const,
  },
};

export default WebSocketDebug;